import {
  PhxColumnGroupData,
  PhxColumnGroupSegment,
  PhxStickyColumnInfo,
  PhxTableColumnConfig,
  PhxTableColumnGroupConfig,
  PhxTableHeaderConfig,
} from './phx-table.types';

/** Rendered width (px) of the auto-drawn checkbox column — must match `.phx-table-control-cell`'s `width` in phx-table.component.scss. */
export const CHECKBOX_COL_WIDTH = 56;

export function isActionTableColumn(key: string | undefined): boolean {
  return key === 'action' || key === 'actions';
}

/** Index of the data column whose `<col>` omits width so it absorbs extra table width. */
export function getFlexibleColumnIndex(visibleColumns: PhxTableColumnConfig[]): number | null {
  if (!visibleColumns.length) {
    return null;
  }

  const lastIndex = visibleColumns.length - 1;
  const lastIsAction = isActionTableColumn(visibleColumns[lastIndex]?.key);
  const flexIndex = lastIsAction ? lastIndex - 1 : lastIndex;

  return flexIndex >= 0 ? flexIndex : null;
}

/** Resolves `<col>` width: fixed for all columns except the flexible absorb column. */
export function getColumnColWidth(
  column: PhxTableColumnConfig,
  columnIndex: number,
  visibleColumns: PhxTableColumnConfig[],
): string | null {
  if (!column.width || getFlexibleColumnIndex(visibleColumns) === columnIndex) {
    return null;
  }

  return column.width;
}

/** Fallback column width (px) used when a column has no width or a non-px width. */
const DEFAULT_COLUMN_WIDTH = 150;

/** Matches a plain `px` length, capturing the numeric part (e.g. `120px`, `12.5px`). */
const PX_WIDTH_PATTERN = /^(\d+(?:\.\d+)?)\s*px$/;

/** Parse a `px` width string to its numeric value, or `null` if it is not a plain px length. */
function parsePxWidth(width: string | undefined): number | null {
  if (!width) return null;
  const match = width.match(PX_WIDTH_PATTERN);
  return match ? parseFloat(match[1]) : null;
}

export function parseColumnWidth(width: string | undefined): number {
  return parsePxWidth(width) ?? DEFAULT_COLUMN_WIDTH;
}

export function computeTableMinWidth(visibleColumns: PhxTableColumnConfig[], selectable: boolean): string | null {
  if (!visibleColumns?.length) return null;
  let total = 0;
  let hasAnyWidth = false;
  for (const col of visibleColumns) {
    if (!col.width) continue;
    const parsed = parsePxWidth(col.width);
    if (parsed === null) return null;
    total += parsed;
    hasAnyWidth = true;
  }
  if (!hasAnyWidth) return null;
  if (selectable) total += CHECKBOX_COL_WIDTH;
  return total + 'px';
}

export function normalizeHeaderConfigs(visibleColumns: PhxTableColumnConfig[]): Map<string, PhxTableHeaderConfig[]> {
  const cache = new Map<string, PhxTableHeaderConfig[]>();
  for (const col of visibleColumns) {
    if (!col.headerConfig) {
      cache.set(col.key, []);
    } else {
      cache.set(col.key, Array.isArray(col.headerConfig) ? col.headerConfig : [col.headerConfig]);
    }
  }
  return cache;
}

export function buildColumnGroupData(
  visibleColumns: PhxTableColumnConfig[],
  visibleColumnIndices: number[],
  columnGroups: PhxTableColumnGroupConfig[],
): PhxColumnGroupData {
  const empty: PhxColumnGroupData = { hasGroups: false, segments: [], rightBorders: new Set() };

  if (!visibleColumns?.length) return empty;
  if (!columnGroups?.length) return empty;

  const groupHeaderByColumnKey = new Map<string, string>();
  for (const group of columnGroups) {
    for (const columnKey of group.columns) {
      groupHeaderByColumnKey.set(columnKey, group.header);
    }
  }

  const segments: PhxColumnGroupSegment[] = [];
  const rightBorders = new Set<number>();

  for (let vi = 0; vi < visibleColumns.length; vi++) {
    const col = visibleColumns[vi];
    const fullIdx = visibleColumnIndices[vi];
    const header = groupHeaderByColumnKey.get(col.key) || '';
    const isUngrouped = !header;
    const previousSegment = segments[segments.length - 1];

    if (previousSegment && previousSegment.header === header && previousSegment.isUngrouped === isUngrouped) {
      previousSegment.colspan++;
      previousSegment.endColIdx = fullIdx;
      continue;
    }

    segments.push({
      header,
      colspan: 1,
      isUngrouped,
      startColIdx: fullIdx,
      endColIdx: fullIdx,
      borderLeft: false,
      borderRight: false,
    });
  }

  if (!segments.some(segment => !segment.isUngrouped)) return empty;

  const lastColIdx = visibleColumnIndices[visibleColumns.length - 1];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const prev = i > 0 ? segments[i - 1] : null;

    if (segment.isUngrouped) {
      // Ungrouped spans are borderless except the table's last column edge.
      segment.borderLeft = false;
      segment.borderRight = segment.endColIdx === lastColIdx;
    } else {
      segment.borderRight = true;
      rightBorders.add(segment.endColIdx);
      // Left border at the start of a grouped run (after checkbox or ungrouped span).
      segment.borderLeft = !prev || prev.isUngrouped;
    }
  }

  return { hasGroups: true, segments, rightBorders };
}

export function buildStickyColumnsMap(
  columns: PhxTableColumnConfig[],
  stickyStart: number | undefined,
  stickyEnd: number | undefined,
  selectable: boolean,
): Map<number, PhxStickyColumnInfo> {
  const map = new Map<number, PhxStickyColumnInfo>();

  if (stickyStart && stickyStart > 0) {
    let offset = 0;

    if (selectable) {
      map.set(-1, { index: -1, side: 'start', offset: `${offset}px`, isEdge: false });
      offset += CHECKBOX_COL_WIDTH;
    }

    for (let i = 0; i < stickyStart && i < columns.length; i++) {
      const isEdge = i === stickyStart - 1;
      map.set(i, { index: i, side: 'start', offset: `${offset}px`, isEdge });
      offset += parseColumnWidth(columns[i].width);
    }
  }

  if (stickyEnd && stickyEnd > 0) {
    let offset = 0;
    const total = columns.length;

    for (let i = total - 1; i >= total - stickyEnd && i >= 0; i--) {
      const isEdge = i === total - stickyEnd;
      map.set(i, { index: i, side: 'end', offset: `${offset}px`, isEdge });
      offset += parseColumnWidth(columns[i].width);
    }
  }

  return map;
}

/** trackBy for column config arrays — stable across visibility toggles and reorder. */
export function trackByColumnKey(_index: number, column: PhxTableColumnConfig): string {
  return column.key;
}
